import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { AuctionHollandPriceUpdate, AuctionResponse } from '@types';
import { useAuctionsApi } from '@api/auctions';
import { Form } from 'react-bootstrap';
import { ControlFeedback, Button, BasePreloader, FormGroup } from '@components';
import { getCurrencyFormat } from '@helpers/formats';
import { getDateTimeFormat } from '@helpers/datetime';


interface Props {
  title: string;
  auction: AuctionResponse;
  getTranslation: (key: string) => string;
  refetchAuction: () => void;
}

const HollandUpdateMinimalBidSection: React.FC<Props> = (props) => {
  const auctionsApi = useAuctionsApi();
  const [actualPriceWillBe, setActualPriceWillBe] = useState<number | null>(null);
  const [data, setData] = useState<AuctionHollandPriceUpdate[]>([]);

  const formik = useFormik({
    onSubmit: () => handleSubmit(),
    validateOnChange: false,
    initialValues: {
      minimalBid: '',
    },
  });

  useEffect(() => {
    loadAllPrices().then();

    return () => {
      auctionsApi.cancelAllRequests();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const value = Number.parseFloat(formik.values.minimalBid as string);

    if (value > 0) {
      setActualPriceWillBe(props.auction.hollandCurrentPrice - value);
    } else {
      setActualPriceWillBe(null);
    }
  }, [formik.values.minimalBid]);

  const handleSubmit = async () => {
    formik.setSubmitting(true);
    try {
      await auctionsApi.auctionHollandUpdateMinimalBid(props.auction.id, actualPriceWillBe || props.auction.hollandCurrentPrice);
      formik.setSubmitting(false);
      props.refetchAuction();
      loadAllPrices().then();
    } catch (err: any) {
      if (auctionsApi.isCancel(err)) {
        return;
      }
      const errors = err.response?.data?.errors || {};
      Object.getOwnPropertyNames(errors).map((prop) => {
        formik.setFieldError(prop, errors[prop][0]);
        return prop;
      });
      formik.setSubmitting(false);
    }
  };

  const loadAllPrices = async () => {
    try {
      const res = await auctionsApi.auctionHollandGetAllPriceUpdates(props.auction.id.toString());
      setData(res.data.data);
    } catch (err: any) {
      if (auctionsApi.isCancel(err)) {
        return;
      }
    }
  };

  return (
    <div className="component-admin-box-content">
      <h2 className="f-weight-300 f-size-25 mb-4">{props.title}</h2>

      {props.auction.parentStatus !== 'ended' && (
        <>
          <span className="h4">Aktuální cena: {getCurrencyFormat(props.auction.hollandCurrentPrice)}</span>
          <Form onSubmit={formik.handleSubmit}>
            <div className="responsive-table-content" style={{ overflow: 'visible' }}>
              <div className="pt-2 d-flex align-items-center">
                <div className="align-items-center d-flex">
                  <FormGroup
                    type="number"
                    name="minimalBid"
                    label={props.getTranslation('tab_holland_auction_minimal_bid')}
                    labelClassName="text-left"
                    error={formik.errors.minimalBid}
                    value={formik.values.minimalBid}
                    onChange={(val: any) => {
                      formik.setFieldValue('minimalBid', val.target.value);
                    }}
                  />

                  <span className="f-size-12 f-weight-400 ml-2 mb-4">Kč</span>
                  {!!formik.errors.minimalBid && (
                    <div className="auction-date-error">
                      <ControlFeedback type="invalid">{formik.errors.minimalBid}</ControlFeedback>
                    </div>
                  )}
                </div>
                <div className="mb-4 ml-3 f-weight-bold">
                  <span>Aktuální cena bude: {getCurrencyFormat(actualPriceWillBe || 0)}</span>
                </div>
              </div>
            </div>

            <div className="mt-5">
              {!formik.isSubmitting ? (
                <Button type="submit" variant="btn-outline-primary" className="float-right"
                        disabled={!formik.values.minimalBid}>
                  Uložit
                </Button>
              ) : (
                <BasePreloader size={29} className="d-inline-block float-right" />
              )}
              <div className="clearfix" />
            </div>
          </Form>
        </>
      )}


      <div>
        <p className="mt-3">
          {props.getTranslation('tab_holland_auction_current_price_history')}
        </p>

        <div className="responsive-table-content w-50">
          <table className="table table-striped table-bordered table-hover">
            <thead>
            <tr>
              <th>{props.getTranslation('tab_holland_auction_time_created')}</th>
              <th>{props.getTranslation('tab_holland_auction_price')}</th>
              <th>{props.getTranslation('tab_holland_auction_user')}</th>
            </tr>
            </thead>
            <tbody>
            {data.map((item, index) => (
              <tr key={index}>
                <td>{getDateTimeFormat(item.timeCreated)}</td>
                <td>{getCurrencyFormat(item.price)}</td>
                <td>{`${item.user?.firstName} ${item.user?.lastName}`}</td>
              </tr>
            ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default HollandUpdateMinimalBidSection;
